import {
    Badge,
    Button,
    Loader,
    Message,
    Modal,
    ModalActions,
    ModalContent,
    ModalHeader,
} from '@/platform/ui';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { router } from '@inertiajs/react';
import dayjs from 'dayjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { quiet } from './quietReload';
import TrailTable from './TrailTable';

// Mirrors ListDgrPortalRunSteps::LIMIT: a full page means there may be more.
const PAGE_SIZE = 200;

const REFRESH_INTERVAL = 15 * 1000;

// Newest first, each step once: a refresh and an earlier page can overlap.
const mergeSteps = (current, incoming) => {
    const byId = new Map((current ?? []).map(step => [step.id, step]));
    incoming.forEach(step => byId.set(step.id, step));

    return [...byId.values()].sort((a, b) => b.id - a.id);
};

/**
 * The full trail of one batch, loaded only when opened: a batch of a thousand
 * look-ups writes thousands of steps, too many to send with every poll. Earlier
 * steps load a page at a time, before the oldest step shown.
 */
const RunStepsModal = ({ runId, run, autoRefresh, onClose }) => {
    const [steps, setSteps] = useState(null);
    const [loadedAt, setLoadedAt] = useState(null);
    const [hasEarlier, setHasEarlier] = useState(false);
    const [failed, setFailed] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingEarlier, setLoadingEarlier] = useState(false);
    const [announcement, setAnnouncement] = useState('');
    const stepsRef = useRef(null);
    stepsRef.current = steps;
    const failedRef = useRef(null);
    failedRef.current = failed;
    // Bumped when a refresh starts the trail again, so an earlier page that
    // was asked for before then cannot be merged into the new window.
    const windowRef = useRef(0);

    const load = useCallback(
        (before = null) => {
            const setBusy = before ? setLoadingEarlier : setLoading;
            const trailWindow = windowRef.current;
            setBusy(true);

            router.reload(
                quiet({
                    only: ['run_steps'],
                    data: before ? { run: runId, before } : { run: runId },
                    preserveUrl: true,
                    onSuccess: page => {
                        if (before && trailWindow !== windowRef.current) {
                            setBusy(false);

                            return;
                        }

                        const incoming = page.props.run_steps ?? [];
                        const current = stepsRef.current;
                        // A refresh that reaches back to the newest step shown
                        // extends the trail. One that does not would leave a
                        // gap, so it starts the trail again from its page.
                        const extendsTrail =
                            before ||
                            (current !== null &&
                                (incoming.length < PAGE_SIZE ||
                                    incoming.at(-1).id <=
                                        (current[0]?.id ?? 0)));

                        if (!extendsTrail) {
                            windowRef.current += 1;
                        }

                        if (failedRef.current) {
                            setAnnouncement('De stappen zijn weer geladen.');
                        }

                        setFailed(null);
                        setLoadedAt(new Date());
                        if (!extendsTrail || before) {
                            setHasEarlier(incoming.length === PAGE_SIZE);
                        }
                        setSteps(
                            extendsTrail
                                ? mergeSteps(current, incoming)
                                : incoming
                        );
                        setBusy(false);
                    },
                    onFailure: () => {
                        setAnnouncement('Laden van de stappen mislukt.');
                        setFailed({ before });
                        setBusy(false);
                    },
                })
            );
        },
        [runId]
    );

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        if (!autoRefresh || !run?.running) {
            return undefined;
        }

        const timer = setInterval(() => load(), REFRESH_INTERVAL);

        return () => clearInterval(timer);
    }, [autoRefresh, run?.running, load]);

    const oldestId = steps?.at(-1)?.id;
    const emptyText = run?.running
        ? 'De batch is gestart, maar er zijn nog geen stappen vastgelegd.'
        : 'Voor deze batch zijn geen stappen vastgelegd.';

    return (
        <Modal
            open={true}
            onClose={onClose}
            size='fullscreen'
            className='max-h-[90vh]'
        >
            <div className='flex flex-wrap items-start justify-between gap-3'>
                <div className='min-w-0'>
                    <ModalHeader>{`Verloop van batch ${runId}`}</ModalHeader>
                    <div className='mt-1 flex flex-wrap items-center gap-2 text-sm text-gray-500'>
                        {run && (
                            <Badge color={run.status_color}>
                                {run.status_label}
                            </Badge>
                        )}
                        {loadedAt && (
                            <span>
                                Stand van {dayjs(loadedAt).format('LTS')}
                            </span>
                        )}
                        <span>Nieuwste eerst, 200 stappen per keer.</span>
                    </div>
                </div>
                <div className='flex shrink-0 items-center gap-2'>
                    <Button loading={loading} onClick={() => load()}>
                        Vernieuwen
                    </Button>
                    <Button aria-label='Verloop sluiten' onClick={onClose}>
                        <XMarkIcon aria-hidden='true' className='size-4' />
                        Sluiten
                    </Button>
                </div>
            </div>

            <div role='status' aria-live='polite' className='sr-only'>
                {announcement}
            </div>

            <ModalContent className='min-h-0 flex-1 overflow-y-auto'>
                {failed && (
                    <Message
                        variant={steps === null ? 'error' : 'warning'}
                        className='mb-3'
                    >
                        <p>
                            {steps === null
                                ? 'De stappen konden niet worden geladen.'
                                : `Laden mislukt. Je ziet de stappen van ${dayjs(loadedAt).format('LTS')}.`}
                        </p>
                        <Button
                            className='mt-2'
                            loading={loading || loadingEarlier}
                            onClick={() => load(failed.before)}
                        >
                            Opnieuw proberen
                        </Button>
                    </Message>
                )}

                {steps !== null && (
                    <TrailTable steps={steps} emptyText={emptyText} />
                )}

                {steps === null && !failed && <Loader />}
            </ModalContent>

            <ModalActions>
                {hasEarlier && oldestId && (
                    <Button
                        loading={loadingEarlier}
                        onClick={() => load(oldestId)}
                    >
                        Eerdere stappen laden
                    </Button>
                )}
                <Button onClick={onClose}>Sluiten</Button>
            </ModalActions>
        </Modal>
    );
};

export default RunStepsModal;
